package org.moxultimate.topwithmox.product.api

import io.ktor.client.*
import io.ktor.client.request.*
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import org.moxultimate.topwithmox.core.base.BaseAPI.baseAPI
import org.moxultimate.topwithmox.core.handler.APIHandler
import org.moxultimate.topwithmox.core.state.APIState
import org.moxultimate.topwithmox.product.data.ProductContentResponse
import org.moxultimate.topwithmox.product.data.ProductContentTagResponse
import org.moxultimate.topwithmox.product.data.ProductContentTypeResponse

/**
 * ProductRepository
 * Handles all read operations for product content, tags, and types.
 * Write operations (create, update, delete, saveTag, saveType) live in ProductCRUDRepository.
 *
 * @param httpClient Ktor HttpClient injected via Koin.
 */
class ProductRepository(
    private val httpClient: HttpClient,
) {
    private val baseUrl = "$baseAPI/product-contents"
    private val tagUrl = "$baseAPI/product-content-tag"
    private val typeUrl = "$baseAPI/product-content-type"

    /**
     * getProductContents
     * Fetches a page of product content items using cursor-based pagination.
     * Pass [beforeId] = Long.MAX_VALUE to load the first/newest page.
     *
     * @param beforeId Pagination cursor — load items with id strictly less than this value.
     * @return Flow emitting Loading → Success or Error.
     */
    fun getProductContents(beforeId: Long = Long.MAX_VALUE): Flow<APIState<List<ProductContentResponse>>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.get(baseUrl) {
                parameter("beforeId", beforeId)
            }
        })
    }

    /**
     * getAllTags
     * Fetches all product content tags from GET /product-content-tag.
     * Used to populate filter chips and search autocomplete suggestions.
     *
     * @return Flow emitting Loading → Success(List<String>) or Error.
     */
    fun getAllTags(): Flow<APIState<List<String>>> = flow {
        emit(APIState.Loading)
        emit(
            APIHandler.safeApiCall<List<ProductContentTagResponse>, List<String>>(
                apiToBeCalled = { httpClient.get(tagUrl) },
                transformer = { entities -> entities.mapNotNull { it.tag }.distinct() },
            )
        )
    }

    /**
     * getAllTypes
     * Fetches all product content types from GET /product-content-type.
     * Used to populate the type filter chip row in the Products page.
     *
     * @return Flow emitting Loading → Success(List<String>) or Error.
     */
    fun getAllTypes(): Flow<APIState<List<String>>> = flow {
        emit(APIState.Loading)
        emit(
            APIHandler.safeApiCall<List<ProductContentTypeResponse>, List<String>>(
                apiToBeCalled = { httpClient.get(typeUrl) },
                transformer = { entities -> entities.mapNotNull { it.type }.distinct() },
            )
        )
    }

    /**
     * getProductContentById
     * Fetches a single product content entry with its full products list via GET /product-contents/{id}.
     *
     * @param id Content ID to fetch.
     * @return Flow emitting Loading → Success(ProductContentResponse) or Error.
     */
    fun getProductContentById(id: Long): Flow<APIState<ProductContentResponse>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall { httpClient.get("$baseUrl/$id") })
    }
}
